import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetchRaw } from '@/auth/api';

/**
 * Q-AMASSA-LINK (2026-10-28) · the web half of the link between this Amassa
 * install and its Quotinc company (docs/quotinc-link-2026-10-28.md).
 *
 * Every Quotinc surface in Amassa (the sidebar entry, the help section, the
 * "From Quotinc" chip on an imported service line, the "Managed in Quotinc"
 * marker in Admin > Users) asks ONE question first: is the link configured
 * on this install? `GET /api/quotinc/link` answers it. While it says no (or
 * fails, or the person is signed out) nothing Quotinc-related renders.
 */

/** Five minutes: the link's configuration only changes with a redeploy. */
const LINK_STALE_MS = 5 * 60_000;
/** Same window as the server's per-(email, quotation) access cache. */
const MARKS_STALE_MS = 5 * 60_000;
/** The server reads at most this many ids per call. */
export const LINE_MARKS_CHUNK = 200;

export interface QuotincLink {
  on: boolean;
  /** `Quotinc:BaseUrl + /app`, http(s) only. Null while the link is dark. */
  appUrl: string | null;
}

export interface QuotincLineMark {
  quotationId: string;
  quotationRef: string | null;
  /** The line was in an earlier send and is missing from the latest one. */
  removed: boolean;
  /** Present ONLY when Quotinc allows this viewer to open the quotation. */
  viewUrl: string | null;
}

export type QuotincLineMarks = Readonly<Record<string, QuotincLineMark>>;

const DARK: QuotincLink = { on: false, appUrl: null };
const NO_MARKS: QuotincLineMarks = Object.freeze({});
const NO_IDS: ReadonlySet<string> = new Set<string>();

/** A link the page may open: http(s) only, never `javascript:` or a relative path. */
export function safeExternalUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!/^https?:\/\//i.test(s)) return null;
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

interface LinkWire {
  ok?: boolean;
  on?: unknown;
  app_url?: unknown;
}

/** Reads the wire shape defensively: anything unexpected is "dark". */
export function parseQuotincLink(body: LinkWire | null | undefined): QuotincLink {
  if (!body || body.on !== true) return DARK;
  const appUrl = safeExternalUrl(body.app_url);
  return appUrl ? { on: true, appUrl } : DARK;
}

interface MarkWire {
  quotation_id?: unknown;
  quotation_ref?: unknown;
  removed?: unknown;
  view_url?: unknown;
}

export function parseQuotincLineMarks(
  body: { marks?: Record<string, MarkWire> | null } | null | undefined,
): Record<string, QuotincLineMark> {
  const out: Record<string, QuotincLineMark> = {};
  const marks = body?.marks;
  if (!marks || typeof marks !== 'object') return out;
  for (const [id, m] of Object.entries(marks)) {
    if (!id || !m || typeof m !== 'object') continue;
    out[id] = {
      quotationId: typeof m.quotation_id === 'string' ? m.quotation_id : '',
      quotationRef:
        typeof m.quotation_ref === 'string' && m.quotation_ref.trim() ? m.quotation_ref.trim() : null,
      removed: m.removed === true,
      viewUrl: safeExternalUrl(m.view_url),
    };
  }
  return out;
}

export async function getQuotincLink(signal?: AbortSignal): Promise<QuotincLink> {
  const body = await apiFetchRaw<LinkWire>('/quotinc/link', { signal });
  return parseQuotincLink(body);
}

export async function getQuotincLineMarks(
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<Record<string, QuotincLineMark>> {
  const out: Record<string, QuotincLineMark> = {};
  for (let i = 0; i < ids.length; i += LINE_MARKS_CHUNK) {
    const chunk = ids.slice(i, i + LINE_MARKS_CHUNK);
    const body = await apiFetchRaw<{ marks?: Record<string, MarkWire> }>(
      `/quotinc/line-marks?ids=${chunk.map(encodeURIComponent).join(',')}`,
      { signal },
    );
    Object.assign(out, parseQuotincLineMarks(body));
  }
  return out;
}

export async function getQuotincManagedUsers(signal?: AbortSignal): Promise<string[]> {
  const body = await apiFetchRaw<{ ids?: unknown }>('/quotinc/managed-users', { signal });
  return Array.isArray(body?.ids) ? body.ids.filter((x): x is string => typeof x === 'string') : [];
}

export const QUOTINC_LINK_QUERY_KEY = ['quotinc', 'link'] as const;

/**
 * Is the Quotinc link configured on this install? One shared cache entry for
 * every surface. A failure (signed out, an older API without the endpoint,
 * a network blip) reads as dark: no Quotinc chrome, never an error.
 */
export function useQuotincLink(enabled = true): QuotincLink {
  const { data } = useQuery({
    queryKey: QUOTINC_LINK_QUERY_KEY,
    queryFn: ({ signal }) => getQuotincLink(signal).catch(() => DARK),
    enabled,
    staleTime: LINK_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return data ?? DARK;
}

/**
 * The Quotinc marks of the visible service lines, fetched ONCE per page of
 * rows (keyed on the sorted id list, chunked at 200), never per row. Only
 * while the link is on. Any error = no marks.
 *
 * `viewer` is the signed-in person's id: "View in Quotinc" is Quotinc's
 * answer FOR THAT PERSON, so the cache entry is theirs alone. Without it a
 * session that expires and a second person signing in on the same tab (no
 * logout, so no cache clear) would read the first person's links for up to
 * five minutes. No viewer = nothing is asked.
 */
export function useQuotincLineMarks(
  ids: readonly string[],
  viewer: string | null | undefined,
): QuotincLineMarks {
  const link = useQuotincLink();
  const key = [...new Set(ids.filter(Boolean))].sort().join(',');
  const sorted = useMemo(() => (key ? key.split(',') : []), [key]);
  const who = (viewer ?? '').trim();
  const { data } = useQuery({
    queryKey: ['quotinc', 'line-marks', who, sorted] as const,
    queryFn: ({ signal }) => getQuotincLineMarks(sorted, signal).catch(() => ({})),
    enabled: link.on && who.length > 0 && sorted.length > 0,
    staleTime: MARKS_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
  return link.on && who.length > 0 && data ? data : NO_MARKS;
}

/** Users whose platform access is managed in Quotinc (admins only). */
export function useQuotincManagedUsers(isAdmin: boolean): ReadonlySet<string> {
  const link = useQuotincLink();
  const { data } = useQuery({
    queryKey: ['quotinc', 'managed-users'] as const,
    queryFn: ({ signal }) => getQuotincManagedUsers(signal).catch(() => [] as string[]),
    enabled: link.on && isAdmin,
    staleTime: LINK_STALE_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const on = link.on && isAdmin;
  return useMemo(() => (on && data ? new Set(data) : NO_IDS), [on, data]);
}
